import { Stack, StackProps, Typography, TypographyProps, Theme } from '@mui/material';
import { ColoredIcon } from '@verifime/components';
import tokens from '@verifime/design-tokens';
import { TStatus } from '@verifime/utils';
import { ReactNode } from 'react';

type TVerificationStatusProps = {
  /**
   * Document statuses reach this component as plain strings, or none at all, so the value is not
   * limited to `TStatus`. Any value outside `STATUS_MAPPING` falls back; see `FALLBACK_STATUS`.
   */
  status?: TStatus | string | null;
  showLabel?: boolean;
  showIcon?: boolean;
};

type TIconProps = Parameters<typeof ColoredIcon>[0];
type TVerificationStatusDisplayProps = {
  color: StackProps['color'];
  label: ReactNode;
  labelProps?: TypographyProps;
  iconName: TIconProps['iconName'];
  iconProps?: Omit<TIconProps, 'iconName'>;
} & Required<Omit<TVerificationStatusProps, 'status'>>;

function VerificationStatusDisplay({
  color,
  showLabel,
  showIcon,
  label,
  labelProps,
  iconName,
  iconProps,
}: TVerificationStatusDisplayProps) {
  return (
    <Stack
      direction="row"
      gap={tokens.spacingXs}
      alignItems="center"
      justifyItems="flex-start"
      color={color}
    >
      {showIcon && <ColoredIcon iconName={iconName} {...iconProps} />}
      {showLabel && <Typography {...labelProps}>{label}</Typography>}
    </Stack>
  );
}

const STATUS_MAPPING: {
  [status in TStatus]: Omit<TVerificationStatusDisplayProps, 'showLabel' | 'showIcon'>;
} = {
  Verified: {
    color: (theme: Theme) => theme.palette.success.main,
    iconName: 'checkCircle',
    label: 'Verified',
  },
  Rejected: {
    color: (theme: Theme) => theme.palette.error.main,
    iconName: 'block',
    label: 'Action required',
  },
  InProgress: {
    color: (theme: Theme) => theme.palette.text.secondary,
    iconName: 'pending',
    label: 'In Progress',
  },
  Expired: {
    color: (theme: Theme) => theme.palette.action.disabled,
    iconName: 'remove',
    label: 'Expired',
  },
  NotAvailable: {
    color: (theme: Theme) => theme.palette.action.disabled,
    iconName: 'remove',
    label: 'Not available',
  },
  Matched: {
    color: (theme: Theme) => theme.palette.error.main,
    iconName: 'block',
    label: 'Matched',
  },
  NotMatched: {
    color: (theme: Theme) => theme.palette.success.main,
    iconName: 'checkCircle',
    label: 'No Match',
  },
};

/**
 * What a value with no entry in `STATUS_MAPPING` draws: the existing `NotAvailable` presentation
 * ("Verification not started" in `TStatus`), a neutral look that is never verified, matched or
 * rejected. Without it the lookup returns undefined and the icon has no component to render, which
 * blanks the whole page (verifime-adr#881).
 */
const FALLBACK_STATUS: TStatus = 'NotAvailable';

function isMappedStatus(status: unknown): status is TStatus {
  // Own keys only: 'constructor' and 'toString' are on every object but are not statuses.
  return typeof status === 'string' && Object.prototype.hasOwnProperty.call(STATUS_MAPPING, status);
}

export default function VerificationStatus({
  status,
  showLabel = true,
  showIcon = true,
}: TVerificationStatusProps) {
  const verificationStatusDisplayProps =
    STATUS_MAPPING[isMappedStatus(status) ? status : FALLBACK_STATUS];
  return (
    <VerificationStatusDisplay
      {...verificationStatusDisplayProps}
      showLabel={showLabel}
      showIcon={showIcon}
    />
  );
}
